import { askHeidiCsatModalOpened } from '@/lib/analytics/scribe/ask-ai';
import { useCallback, useState } from 'react';
import type { FeedbackType } from '../components/customer-feedback';

interface UseCustomerFeedbackProps {
  initialFeedback?: FeedbackType;
  mode?: string;
}

interface UseCustomerFeedbackReturn {
  // State
  selectedFeedback: FeedbackType;
  feedbackMessage: string;
  isModalOpen: boolean;
  feedbackComment: string;
  pendingFeedback: FeedbackType;
  isSubmitDisabled: boolean;

  // Actions
  handleFeedbackClick: (feedback: FeedbackType) => void;
  handleSubmit: (
    submitFeedback: (feedback: FeedbackType, comment: string) => Promise<void>
  ) => Promise<void>;
  handleCancel: () => void;
  setFeedbackComment: (comment: string) => void;
}

export const useCustomerFeedback = ({
  initialFeedback = null,
  mode,
}: UseCustomerFeedbackProps): UseCustomerFeedbackReturn => {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(initialFeedback);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [pendingFeedback, setPendingFeedback] = useState<FeedbackType>(null);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingFeedback(null);
    setFeedbackComment('');
  }, []);

  const handleFeedbackClick = useCallback(
    (feedback: FeedbackType) => {
      // If clicking the same feedback type and modal is open, close it
      if (selectedFeedback === feedback && isModalOpen) {
        closeModal();
        return;
      }

      // If clicking the same feedback type and modal is closed, open it to allow editing
      if (selectedFeedback === feedback && !isModalOpen) {
        setPendingFeedback(feedback);
        setFeedbackComment(feedbackMessage); // Pre-fill with existing message
        setIsModalOpen(true);

        // Track modal opened
        if (feedback) {
          askHeidiCsatModalOpened({
            mode,
            feedbackType: feedback,
          });
        }
        return;
      }

      // Set pending feedback and open modal for new feedback
      setPendingFeedback(feedback);
      setFeedbackComment(''); // Clear comment for new feedback
      setIsModalOpen(true);

      // Track modal opened
      if (feedback) {
        askHeidiCsatModalOpened({
          mode,
          feedbackType: feedback,
        });
      }
    },
    [selectedFeedback, isModalOpen, feedbackMessage, closeModal, mode]
  );

  const handleSubmit = useCallback(
    async (
      submitFeedback: (feedback: FeedbackType, comment: string) => Promise<void>
    ) => {
      if (pendingFeedback && feedbackComment.trim()) {
        setSelectedFeedback(pendingFeedback);
        setFeedbackMessage(feedbackComment.trim());

        // Use the provided submitFeedback function
        await submitFeedback(pendingFeedback, feedbackComment.trim());

        closeModal();
      }
    },
    [pendingFeedback, feedbackComment, closeModal]
  );

  const handleCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const isSubmitDisabled = !feedbackComment.trim();

  return {
    // State
    selectedFeedback,
    feedbackMessage,
    isModalOpen,
    feedbackComment,
    pendingFeedback,
    isSubmitDisabled,

    // Actions
    handleFeedbackClick,
    handleSubmit,
    handleCancel,
    setFeedbackComment,
  };
};
